import * as React from "react";
import { useEffect } from "react";
import { getTokenMakerUrl } from ".";
import { useIdentity } from "../containers/IdentityContainer";

type LogLevel = "log" | "error" | "warn" | "info" | "debug";

// Track if we're currently sending to prevent recursive calls
let isSending = false;
// Track failed attempts to avoid spamming logs
let failedAttempts = 0;
const MAX_FAILED_ATTEMPTS = 3;

const sendToBackend = async (
  level: LogLevel,
  message: any[],
  token: string
) => {
  // Skip sending logs if there's no valid token, we're already sending, or too many failures
  if (!token || isSending || failedAttempts >= MAX_FAILED_ATTEMPTS) {
    return;
  }
  
  const url = getTokenMakerUrl();
  
  // Create an AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
  
  try {
    isSending = true;
    const response = await fetch(`${url}/logs`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        level,
        message: message.map((m) => {
          try {
            if (m instanceof Error) {
              return JSON.stringify({
                name: m.name,
                message: m.message,
                stack: m.stack,
              });
            }
            return typeof m === "object" ? JSON.stringify(m) : m;
          } catch {
            return "[Circular or unserializable object]";
          }
        }),
        timestamp: new Date().toISOString(),
      }),
      signal: controller.signal,
    });
    
    // Check if the response is ok
    if (!response.ok) {
      failedAttempts++;
      // Log locally but don't try to send to backend again
      if (failedAttempts === 1) {
        originalConsole.warn(`Log backend returned ${response.status}`);
      }
    } else {
      // Reset failed attempts on success
      failedAttempts = 0;
    }
  } catch (err: any) {
    failedAttempts++;
    // Only log the first failure to avoid spam
    if (failedAttempts === 1) {
      if (err.name === 'AbortError') {
        originalConsole.warn("Log backend request timed out");
      } else {
        originalConsole.warn("Failed to send log to backend:", err.message || err);
      }
    }
  } finally {
    clearTimeout(timeoutId);
    isSending = false;
  }
};

// Save a reference to the original console methods
const originalConsole = { ...console };

// Override console methods
export const overrideConsole = (token: string) => {
  ["log", "error", "warn", "info", "debug"].forEach((level) => {
    (console as any)[level] = (...args: any[]) => {
      // 1. Log to the original front-end console
      (originalConsole as any)[level](...args);

      // 2. Send the logs to the backend only if there's a valid token
      // Don't send logs that originate from the logging system itself
      const isLoggingError = args.some(arg => 
        typeof arg === 'string' && 
        (arg.includes('Failed to send log to backend') || 
         arg.includes('Log backend returned'))
      );
      
      if (token && !isLoggingError) {
        sendToBackend(level as LogLevel, args, token);
      }
    };
  });
};

type Props = {
  children: React.ReactNode;
};
export const FlingLogsContainer: React.FC<Props> = ({ children }) => {
  const { airplxToken } = useIdentity();
  useEffect(() => {
    overrideConsole(airplxToken);
  }, [airplxToken]);

  return <>{children}</>;
};
